<template>
  <div
    :class="[
      brandClasses.container,
      {
        'w-full': layout === 'horizontal',
        'f-col': layout === 'vertical',
      },
    ]"
    @click="$emit('recClick', upsell.id)"
  >
    <base-link aria-hidden="true" class="shrink-0" tabindex="-1" :to="upsell.url" @click="handleClick()">
      <base-picture
        :alt="upsell.name"
        :height="image.height"
        :src="getImageTransformations(upsell.image, image)"
        :width="image.width"
      />
    </base-link>
    <div
      class="f-col between"
      :class="{ 'h-full': layout === 'vertical' }"
      :style="layout === 'vertical' ? { width: pxToRem(image.width) } : undefined"
    >
      <div>
        <vf-link :class="brandClasses.name" :to="upsell.url" variant="quiet" @click="handleClick()">
          {{ upsell.name }}
        </vf-link>
        <product-pricing
          :class="brandClasses.price"
          compact-omnibus
          :currency
          :price="upsell.price"
          :show-discount-percentage="$feature.showDiscountPercentageOnCatalog"
          show-dynamic-pricing-on-plp
        />
      </div>
      <vf-button
        class="self-start"
        :class="brandClasses.cta"
        :loading
        :size="buttonSize"
        :variant="buttonVariant"
        @click="handleAddToCart"
      >
        {{ $t.productUpsellAddToCart }}
      </vf-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Product, ProductVariants } from '#root/api/clients/product/data-contracts'
import type { ProductClickAction } from '#types/gtm'
import type { ProductUpsell } from '#types/product'
import { ApiErrorCode } from '#root/enums/api'

export type Props = {
  upsell: ProductUpsell
  suppressMiniCart?: boolean
  layout?: 'horizontal' | 'vertical'
}

export type Emits = {
  openQuickshop: [product: Product]
  recClick: [id: string, action?: ProductClickAction]
}

const { layout = 'horizontal', suppressMiniCart, upsell } = defineProps<Props>()
const emit = defineEmits<Emits>()

const { brandClasses, buttonSize, buttonVariant, image } = useAppConfig().components.product.upsell
const { $gtm, $locale, $t } = useNuxtApp()
const currency = useCurrencyCode($locale)
const cart = useCartStore()

const loading = ref(false)

const handleClick = (action: ProductClickAction = 'Navigate To PDP') => {
  emit('recClick', upsell.id, action)
  $gtm.push('upsell.onCrossSellDetails', upsell.id)
}

const handleAddToCart = async () => {
  loading.value = true

  setInteractionOrigin('up-sell-complete-the-look', true)

  if (upsell.multiVariantsProduct) {
    handleClick('Open Quick Shop')

    emit('openQuickshop', {
      ...upsell,
      currency,
      attributes: [],
      variants: {} as ProductVariants
    })

    loading.value = false
  }
  else {
    try {
      await withRetryAfterTime(() => cart.add({
        product: upsell.sku,
        suppressMiniCart
      }), 2000, [ApiErrorCode.GWP_PRODUCT])
    }
    catch (err) {
      log.error(err)
    }
    finally {
      loading.value = false
      resetInteractionOrigin()
    }
  }
}
</script>
